import React, { useEffect, useState } from 'react'
import { inject } from 'mobx-react'
import classNames from 'classnames'
import { Button, Checkbox, Dimmer, Form, Grid, Loader, Table } from 'semantic-ui-react'
import Section from '../../UI/Section'
import { Time } from '../../misc/Times'
import './styles'

const OcBotTestDetail = ({ actions, conversationId }) => {
  const [conversation, setConversation] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [agentMessage, setAgentMessage] = useState('')
  const [isFollowup, setIsFollowup] = useState(false)
  const [isPremium, setIsPremium] = useState(false)
  const [sending, setSending] = useState(false)
  const [sendError, setSendError] = useState(null)

  const load = async() => {
    setLoading(true)
    setError(null)
    const [result, err] = await actions.admin.getOcBotTestConversation(conversationId)
    setLoading(false)
    if(err) {
      setError('Error fetching the conversation')
      return
    }
    setConversation(result)
    setIsPremium(result.isPremium)
  }

  useEffect(() => {
    load()
  }, [conversationId])

  const send = async() => {
    setSending(true)
    setSendError(null)
    const [result, err] = await actions.admin.generateOcBotTestResponse({ conversationId, agentMessage, isFollowup, isPremium })
    setSending(false)
    setIsFollowup(false)
    if(err) {
      setSendError('Could not generate a reply')
      return
    }
    setConversation(result.conversation)
    setIsPremium(result.conversation.isPremium)
    setAgentMessage('')
  }

  const renderProfileExtras = (profile) => {
    const classifiers = profile.classifiers || {}
    const classifierKeys = Object.keys(classifiers)
    const imgs = profile.profileImgs || []
    return (
      <>
        {profile.gender && <Grid.Row>Gender: <span className='bold'>{profile.gender}</span></Grid.Row>}
        {profile.billingCycle != null && <Grid.Row>Billing cycle: <span className='bold'>{profile.billingCycle}</span></Grid.Row>}
        {profile.remainingCredits != null && <Grid.Row>Remaining credits: <span className='bold'>{profile.remainingCredits}</span></Grid.Row>}
        {imgs.length > 0 && (
          <Grid.Row>Profile images: <span className='bold'>{imgs.length}</span>
            {imgs.map((url, i) => (
              <div key={i}><a href={url} target='_blank' rel='noopener noreferrer'>{url}</a></div>
            ))}
          </Grid.Row>
        )}
        {classifierKeys.length > 0 && <Grid.Row>Profile fields: <span className='bold'>{classifierKeys.map((key) => `${key}: ${classifiers[key]}`).join(', ')}</span></Grid.Row>}
      </>
    )
  }

  if(loading || !conversation) {
    return (
      <div className='content' id='ocBotTest-detail'>
        <Dimmer active inline='centered'>
          <Loader>Loading</Loader>
        </Dimmer>
        {error && <h4>{error}</h4>}
      </div>
    )
  }

  return (
    <div className='content' id='ocBotTest-detail'>
      <div className='content-body'>
        <h2>OC bot test conversation #{conversation.id}</h2>
        <Section>
          <Grid columns={2}>
            <Grid.Column>
              <Grid.Row>White label uid: <span className='bold'>{conversation.whiteLabelUid}</span></Grid.Row>
              <Grid.Row>Premium: <span className='bold'>{conversation.isPremium ? 'Yes' : 'No'}</span></Grid.Row>
              <Grid.Row>Bot persona: <span className='bold'>{conversation.user0Profile.username}</span>, {conversation.user0Profile.age}, {conversation.user0Profile.location}</Grid.Row>
              <Grid.Row>{conversation.user0Profile.description}</Grid.Row>
              {renderProfileExtras(conversation.user0Profile)}
            </Grid.Column>
            <Grid.Column>
              <Grid.Row>Agent messages: <span className='bold'>{conversation.messageCounter ? conversation.messageCounter.agentMessages : 0}</span></Grid.Row>
              <Grid.Row>User messages: <span className='bold'>{conversation.messageCounter ? conversation.messageCounter.userMessages : 0}</span></Grid.Row>
              <Grid.Row>Simulated user: <span className='bold'>{conversation.user1Profile.username}</span>, {conversation.user1Profile.age}, {conversation.user1Profile.location}</Grid.Row>
              <Grid.Row>{conversation.user1Profile.description}</Grid.Row>
              {renderProfileExtras(conversation.user1Profile)}
            </Grid.Column>
          </Grid>
        </Section>
        <Section>
          <Table celled striped>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Sent at</Table.HeaderCell>
                <Table.HeaderCell>Sent by</Table.HeaderCell>
                <Table.HeaderCell>Message</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {conversation.messages.map((message, i) => (
                <Table.Row key={i} className={classNames({ 'message-sentBy-bot': message.sentBy === 0, 'message-sentBy-user': message.sentBy === 1, 'message-sentBy-system': message.sentBy === 2 })}>
                  <Table.Cell><Time time={message.sentAt} format='MMM DD YYYY HH:mm:ss'/></Table.Cell>
                  <Table.Cell>{message.sentBy === 0 ? 'Bot' : message.sentBy === 1 ? 'Simulated user' : 'System'}</Table.Cell>
                  <Table.Cell>{message.message}</Table.Cell>
                </Table.Row>
              ))}
              {conversation.messages.length === 0 && (
                <Table.Row>
                  <Table.Cell colSpan={3}>No messages yet.</Table.Cell>
                </Table.Row>
              )}
            </Table.Body>
          </Table>
        </Section>
        <Section title='Send a message'>
          <Form loading={sending} error={!!sendError} onSubmit={send}>
            <Form.Field>
              <label>Message from the simulated user (leave blank to just nudge the bot)</label>
              <input name='agentMessage' value={agentMessage} onChange={(e) => setAgentMessage(e.target.value)} disabled={isFollowup}/>
            </Form.Field>
            <Form.Field>
              <Checkbox label='Follow up (nudge the bot without a new message)' checked={isFollowup} onChange={(e, data) => { setIsFollowup(data.checked); if (data.checked) { setAgentMessage('') } }}/>
            </Form.Field>
            <Form.Field>
              <Checkbox label='Premium' checked={isPremium} onChange={(e, data) => setIsPremium(data.checked)}/>
            </Form.Field>
            {sendError && <div style={{ color: '#9f3a38', marginBottom: '1em' }}>{sendError}</div>}
            <Button primary type='submit' loading={sending}>Send &amp; generate reply</Button>
          </Form>
        </Section>
      </div>
    </div>
  )
}

export default inject('store', 'actions')(OcBotTestDetail)
